import { c as _c } from "react/compiler-runtime";
import { AspectRatio, Box, Button, Card, CloseButton, Dialog, Flex, Heading, HStack, Icon, Image, Portal, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { BECOME_ADVISOR_LANDING_ILLUSTRATIONS } from '@src/components/sco/Advisors/AdvisorsLandingIllustrations';
import { ClarityAdvisorImportDiagnostic } from '@src/components/sco/Advisors/ClarityAdvisorImportDiagnostic';
import { useClarityAdvisorImport } from '@src/components/sco/Advisors/hooks/useClarityAdvisorImport';
import { STANDALONE_ADVISORS_PATHS } from '@src/components/sco/Advisors/paths';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericFAQ from '@src/components/sco/GenericLP/GenericFAQ';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import GenericSVG from '@src/components/sco/GenericLP/GenericSVG';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import { TextLink } from '@src/components/sco/TextLink';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import { startupsPath } from '@src/lib/startups-url';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuChevronRight, LuCircleCheck } from 'react-icons/lu';
const variantKey = 'advisors-become';
const CLARITY_VARIANT_KEY = 'advisors-become-clarity';
const SECTION_BG = 'colorPalette.bg';
const APPLY_HREF = STANDALONE_ADVISORS_PATHS.apply;
const CTA_LABEL = 'Apply to be an advisor';
const HERO_CTA_LABEL = 'Apply to become an advisor';
const CLARITY_HERO_HEADING = 'Clarity.fm experts, join Startups.com Advisors.';
const CLARITY_HERO_GRADIENT = 'Startups.com Advisors.';
const CLARITY_HERO_SUBHEADING = 'Bring the profile you already have. Advise founders for equity, paid sessions, or both.';
const CLARITY_SECTION_HEADING = 'Build a startup portfolio doing what you love.';
const CLARITY_SECTION_SUBHEADING = 'Same expertise you share on Clarity. Match with founders, take board seats, and get paid for sessions.';
const CLARITY_FAQ = {
  question: 'What happens to my Clarity.fm account?',
  answer: 'Nothing changes there. We only use your public Clarity profile to start a Startups.com advisor application. Your Clarity calls stay on Clarity.fm.'
};
type BecomeAdvisorHeroCardProps = {
  title: string;
  description: string;
  imageUrl: string;
};
const HERO_CARDS: BecomeAdvisorHeroCardProps[] = [{
  title: 'Match',
  description: 'Get matched to startups that need exactly what you know, at the stage and in the industry you love.',
  imageUrl: '/assets/illustration/become-advisor/become-advisor-match.svg'
}, {
  title: 'Advise',
  description: 'Help Founders on your terms, for equity, cash, or both, however you like to show up.',
  imageUrl: '/assets/illustration/become-advisor/become-advisor-advise.svg'
}, {
  title: 'Build',
  description: 'Grow a portfolio of startups you back, and the equity you earn as they succeed.',
  imageUrl: '/assets/illustration/become-advisor/become-advisor-build.svg'
}];
const PAGE_TITLE = 'Become a Startup Advisor';
const PAGE_DESCRIPTION = 'Join the Startups.com advisor network. Take board seats for equity, get paid for sessions, and get matched to founders who need your expertise.';
const buildMatchingBlocks = (openApply: () => void, ctaLabel: string = CTA_LABEL): GenericContentBlockConfig[] => [{
  eyebrow: 'Startup Matching',
  heading: 'Startups need what you know.',
  paragraph: 'Your knowledge, connections, and experience are incredibly valuable to startups, especially at the early stages. We match you to Founders who need exactly that.',
  listItems: ['Your relationships and know-how have real value', 'Matched on your domain and industry expertise', 'Meet startups that value your experience'],
  primaryCta: {
    label: ctaLabel,
    onClick: openApply
  },
  visual: <GenericSVG idPrefix={'balp'} illustrationIndex={0} illustrations={BECOME_ADVISOR_LANDING_ILLUSTRATIONS} />
}, {
  eyebrow: 'Direct Intros',
  heading: 'Founders come to you.',
  paragraph: "You're introduced straight to Founders who want your help, no cold pitching, no chasing deals.",
  listItems: ['Talk 1:1 with Founders who fit your expertise and needs', 'No pitching necessary, Founders are looking for you', 'All communications happen directly'],
  primaryCta: {
    label: ctaLabel,
    onClick: openApply
  },
  visual: <GenericSVG idPrefix={'balp'} illustrationIndex={1} illustrations={BECOME_ADVISOR_LANDING_ILLUSTRATIONS} />
}, {
  eyebrow: 'On Your Terms',
  heading: 'Advise on your schedule.',
  paragraph: 'You decide how you show up and how much time you give, direct messages, video calls, or in person, on the schedule that fits your life.',
  listItems: ['Direct messages, video calls, or in person', 'Set how many hours you want to give', 'Total flexibility, on your schedule'],
  primaryCta: {
    label: ctaLabel,
    onClick: openApply
  },
  visual: <GenericSVG idPrefix={'balp'} illustrationIndex={2} illustrations={BECOME_ADVISOR_LANDING_ILLUSTRATIONS} />
}, {
  eyebrow: 'Equity Agreements',
  heading: 'Your equity, papered for you.',
  paragraph: 'Standard FAST agreements with vesting handled, so your advisory equity is real and on the cap table.',
  listItems: ['Equity agreements set up online', 'All vesting is handled inside the agreement'],
  primaryCta: {
    label: ctaLabel,
    onClick: openApply
  },
  visual: <GenericSVG idPrefix={'balp'} illustrationIndex={3} illustrations={BECOME_ADVISOR_LANDING_ILLUSTRATIONS} />
}, {
  eyebrow: 'Compensation',
  heading: 'Cash? Equity? Your call.',
  paragraph: 'Choose how you want to be compensated for your time and value, cash, equity, or even pro bono work to build relationships.',
  listItems: ['Paid sessions billed through the platform; you keep 80–90% depending on your plan', 'Equity grants handled seamlessly online', 'Pro bono work to give back to the community'],
  primaryCta: {
    label: ctaLabel,
    onClick: openApply
  },
  visual: <GenericSVG idPrefix={'balp'} illustrationIndex={4} illustrations={BECOME_ADVISOR_LANDING_ILLUSTRATIONS} />
}];
const faqItems = [{
  question: 'Am I qualified to be an advisor?',
  answer: 'Qualification is simply whether you have knowledge or connections an aspiring startup would find valuable. It’s not about being a “seasoned veteran,” it’s about being able to make an introduction to a customer you’ve worked with before, or explain how you do what you do for a living.'
}, {
  question: 'How do you match me with startups?',
  answer: 'A short profile captures your expertise, the stages and industries you know, and your terms. We then introduce you to startups that fit, no cold pitching required.'
}, {
  question: 'Do I get paid in equity, cash, or both?',
  answer: 'Both. Board seats are equity, papered with standard FAST agreements and vesting handled for you. Paid sessions are cash: founders book you at your listed rate and we withhold 20%, 15%, or 10% from your payout depending on your plan. You can offer one, the other, or both.'
}, {
  question: 'How do the introductions work?',
  answer: 'When a match is made, we send a direct message between you and the Founder to begin the relationship.'
}, {
  question: "What if a startup isn't the right fit?",
  answer: 'No obligation. Take the startups you want and pass on the rest, your portfolio is yours to shape.'
}, {
  question: 'Is there a cost to become an advisor?',
  answer: 'You can apply for free. To go live in the directory, you need Startups Pro, the same plan founders already use. There’s no separate advisor fee.'
}, {
  question: "Are there any fees when I'm compensated?",
  answer: 'No platform fee on equity. For paid sessions, founders pay your listed rate. We withhold 20% on Free, 15% on Startups Pro, and 10% on Advisor Team.'
}];
const BecomeAdvisorHeroCard: React.FC<BecomeAdvisorHeroCardProps> = t0 => {
  const $ = _c(23);
  const {
    title,
    description,
    imageUrl
  } = t0;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t1;
  if ($[0] !== getBreakpointValue) {
    t1 = getBreakpointValue([3, 4, 4]);
    $[0] = getBreakpointValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cardPaddingX = t1;
  let t2;
  if ($[2] !== getBreakpointValue) {
    t2 = getBreakpointValue([3, 4, 4]);
    $[2] = getBreakpointValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cardPaddingTop = t2;
  let t3;
  if ($[4] !== getBreakpointValue) {
    t3 = getBreakpointValue([4, 4, 5]);
    $[4] = getBreakpointValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const cardPaddingBottom = t3;
  let t4;
  if ($[6] !== imageUrl || $[7] !== title) {
    t4 = <AspectRatio ratio={1.7777777777777777} width="full"><Image alt={title} bg="colorPalette.bg" objectFit="contain" src={imageUrl} /></AspectRatio>;
    $[6] = imageUrl;
    $[7] = title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["xl", "xl", "2xl"];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== title) {
    t6 = <Heading as="h3" fontSize={t5} fontWeight="extrabold" letterSpacing="-0.04em" lineHeight="1.1">{title}</Heading>;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== description) {
    t7 = <Text color="fg.muted" flex={1} fontSize="sm" lineHeight="1.3">{description}</Text>;
    $[12] = description;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== cardPaddingBottom || $[15] !== cardPaddingTop || $[16] !== cardPaddingX || $[17] !== t6 || $[18] !== t7) {
    t8 = <VStack align="stretch" flex={1} gap={2} paddingBottom={cardPaddingBottom} paddingTop={cardPaddingTop} paddingX={cardPaddingX} textAlign="center">{t6}{t7}</VStack>;
    $[14] = cardPaddingBottom;
    $[15] = cardPaddingTop;
    $[16] = cardPaddingX;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t8) {
    t9 = <VStack align="stretch" bg="colorPalette.container" borderColor="border.emphasized" borderRadius="2xl" borderWidth="1px" boxShadow="md" color="fg" flex={1} gap={0} height="full" minWidth={0} overflow="hidden">{t4}{t8}</VStack>;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
const BecomeAdvisorHeroCards: React.FC<{
  cards: BecomeAdvisorHeroCardProps[];
}> = t0 => {
  const $ = _c(14);
  const {
    cards
  } = t0;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t1;
  if ($[0] !== getBreakpointValue) {
    t1 = getBreakpointValue([true, true, false]);
    $[0] = getBreakpointValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isStacked = t1;
  let t2;
  if ($[2] !== getBreakpointValue) {
    t2 = getBreakpointValue([3, 4, 4]);
    $[2] = getBreakpointValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const gap = t2;
  const t3 = isStacked ? "column" : "row";
  let t4;
  if ($[4] !== cards || $[5] !== isStacked) {
    let t5;
    if ($[7] !== cards.length || $[8] !== isStacked) {
      t5 = (card, index) => <React.Fragment key={card.title}><BecomeAdvisorHeroCard description={card.description} imageUrl={card.imageUrl} title={card.title} />{index < cards.length - 1 && <Flex align="center" aria-hidden={true} color="fg.subtle" flexShrink={0} justify="center" transform={isStacked ? "rotate(90deg)" : undefined}><Icon as={LuChevronRight} boxSize={5} /></Flex>}</React.Fragment>;
      $[7] = cards.length;
      $[8] = isStacked;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = cards.map(t5);
    $[4] = cards;
    $[5] = isStacked;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[10] !== gap || $[11] !== t3 || $[12] !== t4) {
    t5 = <Flex align="stretch" direction={t3} gap={gap} width="full">{t4}</Flex>;
    $[10] = gap;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export const BecomeAdvisorLandingSection = () => {
  const $ = _c(58);
  const activeContent = useActiveContentContext();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const router = useRouter();
  const screenname = typeof router.query.screenname === "string" ? router.query.screenname : "";
  const isClarity = !!screenname;
  const clarityImport = useClarityAdvisorImport(screenname);
  const [applyOpen, setApplyOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const canImportClarity = isClarity && clarityImport.hasImportableExpert;
  const ctaLabel = canImportClarity ? clarityImport.importCtaLabel : CTA_LABEL;
  let t0;
  if ($[0] !== canImportClarity) {
    t0 = () => {
      if (canImportClarity) {
        setClaimOpen(true);
        return;
      }
      setApplyOpen(true);
    };
    $[0] = canImportClarity;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const openApply = t0;
  let t1;
  if ($[2] !== ctaLabel || $[3] !== openApply) {
    t1 = buildMatchingBlocks(openApply, ctaLabel);
    $[2] = ctaLabel;
    $[3] = openApply;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const matchingBlocks = t1;
  let t2;
  if ($[5] !== isClarity) {
    t2 = isClarity ? [CLARITY_FAQ, ...faqItems] : faqItems;
    $[5] = isClarity;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const landingFaq = t2;
  let t3;
  let t4;
  if ($[7] !== clarityImport.canonicalScreenName || $[8] !== isClarity || $[9] !== router || $[10] !== screenname) {
    t3 = () => {
      if (!isClarity || !clarityImport.canonicalScreenName) {
        return;
      }
      if (clarityImport.canonicalScreenName !== screenname) {
        router.replace(STANDALONE_ADVISORS_PATHS.becomeAnAdvisorClarity(clarityImport.canonicalScreenName), undefined, {
          shallow: true
        });
      }
    };
    t4 = [clarityImport.canonicalScreenName, isClarity, router, screenname];
    $[7] = clarityImport.canonicalScreenName;
    $[8] = isClarity;
    $[9] = router;
    $[10] = screenname;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== activeContent || $[14] !== isClarity) {
    t5 = () => {
      activeContent.setSiteSection(isClarity ? "advisors.become-an-advisor-clarity" : "advisors.become-a-startup-advisor");
      activeContent.setItem({
        __typename: "LandingPage",
        title: isClarity ? CLARITY_HERO_HEADING.replace(/\.$/, "") : PAGE_TITLE,
        description: isClarity ? CLARITY_HERO_SUBHEADING : PAGE_DESCRIPTION
      });
      return () => {
        activeContent.setItem(null);
      };
    };
    $[13] = activeContent;
    $[14] = isClarity;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== isClarity) {
    t6 = [isClarity];
    $[16] = isClarity;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useUniversalEffect(t5, t6);
  const t7 = isClarity ? "advisors-become-clarity-landing" : "advisors-become-landing";
  let t8;
  if ($[18] !== t7) {
    t8 = <TrackEvent action="lpViewed" onlyOnScreen={true} traits={{
      variantKey: t7
    }} />;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = isClarity ? CLARITY_VARIANT_KEY : variantKey;
  let t10;
  if ($[20] !== t9) {
    t10 = <TrackEvent action="lpViewed" onlyOnScreen={true} traits={{
      variantKey: t9
    }} />;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isClarity ? CLARITY_HERO_GRADIENT : "Now earn the Equity.";
  const t12 = isClarity ? CLARITY_HERO_HEADING : "You've earned the Experience. Now earn the Equity.";
  let t13;
  if ($[22] !== isClarity) {
    t13 = isClarity ? {
      maxWidth: "5xl",
      paddingBottom: [8, 10, 12],
      paddingTop: [6, 8, 10]
    } : {
      maxWidth: "5xl"
    };
    $[22] = isClarity;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = isClarity ? CLARITY_HERO_SUBHEADING : "Become a Startup Advisor using what you already know to earn Equity and Cash.";
  let t15;
  if ($[24] !== claimOpen || $[25] !== getBreakpointValue || $[26] !== isClarity || $[27] !== openApply || $[28] !== screenname) {
    t15 = isClarity ? <ClarityAdvisorImportDiagnostic focused={claimOpen} handle={screenname} onDismiss={() => setClaimOpen(false)} /> : <VStack align="center" gap={getBreakpointValue([6, 8, 10])} width="full"><BecomeAdvisorHeroCards cards={HERO_CARDS} /><Button size={getBreakpointValue(["lg", "lg", "xl"])} variant="cta" onClick={openApply}>{HERO_CTA_LABEL}<Icon as={LuChevronRight} /></Button></VStack>;
    $[24] = claimOpen;
    $[25] = getBreakpointValue;
    $[26] = isClarity;
    $[27] = openApply;
    $[28] = screenname;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t15) {
    t16 = <GenericHero gradientText={t11} heading={t12} containerProps={t13} subheading={t14}>{t15}</GenericHero>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = isClarity ? CLARITY_SECTION_HEADING : "Build a Startup Portfolio doing what you Love.";
  const t18 = isClarity ? CLARITY_SECTION_SUBHEADING : "Match with startups that need you, earn equity and cash, and build a portfolio of companies you helped grow.";
  let t19;
  if ($[36] !== matchingBlocks) {
    t19 = <GenericContentBlocks blocks={matchingBlocks} />;
    $[36] = matchingBlocks;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t17 || $[39] !== t18 || $[40] !== t19) {
    t20 = <GenericSection background={SECTION_BG} heading={t17} subheading={t18}>{t19}</GenericSection>;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== landingFaq) {
    t21 = <GenericFAQ heading="Frequent Questions" items={landingFaq} subheading="Want to know more? Here's some common questions & answers." />;
    $[42] = landingFaq;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = event => setApplyOpen(event.open);
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Dialog.Backdrop backdropFilter="blur(10px)" bg="colorPalette.bg/50" />;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [3, 4, 6];
    t25 = [4, 6];
    $[46] = t24;
    $[47] = t25;
  } else {
    t24 = $[46];
    t25 = $[47];
  }
  let t26;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Portal>{t23}<Dialog.Positioner paddingX={t24} paddingY={t25}><Dialog.Content bg="transparent" boxShadow="none" maxWidth="container.lg" overflow="visible" width="full"><Box position="relative"><Dialog.CloseTrigger asChild={true} position="absolute" right={3} top={3} zIndex={2}><CloseButton bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="full" borderWidth="1px" size="sm" /></Dialog.CloseTrigger><BecomeAdvisorSignup id={undefined} /></Box></Dialog.Content></Dialog.Positioner></Portal>;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== applyOpen) {
    t27 = <Dialog.Root motionPreset="scale" open={applyOpen} placement="center" scrollBehavior="inside" size="xl" onOpenChange={t22}>{t26}</Dialog.Root>;
    $[49] = applyOpen;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t10 || $[52] !== t16 || $[53] !== t20 || $[54] !== t21 || $[55] !== t27 || $[56] !== t8) {
    t28 = <Box color="fg" width="full">{t8}{t10}{t16}{t20}{t21}{t27}</Box>;
    $[51] = t10;
    $[52] = t16;
    $[53] = t20;
    $[54] = t21;
    $[55] = t27;
    $[56] = t8;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  return t28;
};
const BecomeAdvisorSignup = t0 => {
  const $ = _c(16);
  const {
    id: t1
  } = t0;
  const id = t1 === undefined ? "signup" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["Get matched to open board seats", "Join boards for equity", "Offer paid sessions for cash", "Chat in Inbox after you join a seat"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const HELP_TOPICS = t2 as const;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [{
      value: "1M+",
      label: "Startups"
    }, {
      value: "$20B",
      label: "Raised"
    }, {
      value: "23M",
      label: "Customers"
    }];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const PROOF_STATS = t3 as const;
  const {
    currentUser
  } = useCurrentUser();
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = startupsPath("/login", {
      returnTo: APPLY_HREF
    });
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const loginHref = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [5, 6, 7];
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      base: 1,
      md: 2
    };
    t7 = {
      base: 8,
      md: 7
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["xl", "2xl", "3xl"];
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Heading as="h2" fontSize={t8} fontWeight="semibold" letterSpacing="-0.05em" lineHeight="1.3">Get paid to help<br /><Box as="span" color="orange.fg">Founders win.</Box></Heading>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text color="fg.subtle" fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase">How advising works here</Text>;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <VStack align="stretch" gap={2.5} width="full">{t10}<VStack align="stretch" gap={2} width="full">{HELP_TOPICS.map(_temp)}</VStack></VStack>;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <VStack align="stretch" gap={5}>{t9}{t11}<Flex align="stretch" bg="bg.subtle" borderColor="border" borderRadius="lg" borderWidth="1px" justify="stretch" overflow="hidden" width="full">{PROOF_STATS.map(_temp2)}</Flex></VStack>;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== currentUser?.isRegistered) {
    t13 = <Card.Body padding={t5}><SimpleGrid alignItems="center" columns={t6} gap={t7}>{t12}<VStack align="stretch" gap={4}>{currentUser?.isRegistered ? <Box textAlign="center"><Heading marginY={4}>You're signed in. Finish your application.</Heading><ButtonLink href={APPLY_HREF} variant="cta">{CTA_LABEL} <Icon as={LuChevronRight} /></ButtonLink></Box> : <VStack align="stretch"><UserSignupEmailCaptureForm containerProps={{
              bg: "colorPalette.card/50",
              width: "full"
            }} hubspotFormKey="scom-advisors-become-landing" returnToHref={APPLY_HREF} socialLoginButtonsProps={{
              buttonLayout: "google-only"
            }} submitButtonProps={{
              children: CTA_LABEL,
              size: "lg"
            }} /><Text color="fg.muted" fontSize="sm" textAlign="center">Already have an account? <TextLink href={loginHref}>Log in</TextLink></Text></VStack>}</VStack></SimpleGrid></Card.Body>;
    $[11] = currentUser?.isRegistered;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== id || $[14] !== t13) {
    t14 = <Card.Root bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="2xl" borderWidth="1px" boxShadow="md" id={id} marginX="auto" maxWidth="container.lg" scrollMarginTop={24} variant="outline" width="full">{t13}</Card.Root>;
    $[13] = id;
    $[14] = t13;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  return t14;
};
function _temp(topic) {
  return <HStack key={topic} align="center" gap={2.5}><Icon as={LuCircleCheck} boxSize={4} color="orange.fg" flexShrink={0} /><Text color="fg" fontSize={["sm", "md"]} fontWeight="medium">{topic}</Text></HStack>;
}
function _temp2(stat, i) {
  return <Box key={stat.label} borderColor="border" borderLeftWidth={i === 0 ? 0 : "1px"} paddingX={[4, 5]} paddingY={3} textAlign="center" width="full"><Text fontSize={["xl", "2xl"]} fontWeight="black" letterSpacing="-0.02em" lineHeight={1}>{stat.value}</Text><Text color="fg.subtle" fontSize="2xs" letterSpacing="0.08em" marginTop={1} textTransform="uppercase">{stat.label}</Text></Box>;
}